import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import styles from './Chat.module.css';
import { ChatInputProps } from './types';
import { DEBUG } from 'lib/util/debug';
import { useChatTranslation } from 'hooks/useChatTranslation';

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  onStartNewConversation,
  onHistory,
}) => {
  const { t, chatLanguage } = useChatTranslation();
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);

  useEffect(() => {
    // Only focus on desktop screens
    if (typeof window !== 'undefined') {
      const isDesktop = window.matchMedia('(pointer: fine)').matches && window.innerWidth >= 768;
      if (isDesktop && textareaRef.current) {
        textareaRef.current.focus();
      }

      // Check for speech recognition support
      const speechSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
      setIsSpeechSupported(speechSupported);
    }
  }, []);

  const adjustTextareaHeight = () => {
    DEBUG.log(
      'ChatInput.adjustTextareaHeight',
      `textarea.scrollHeight: ${textareaRef.current?.scrollHeight}`
    );

    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Adjust textarea height when resizing occurs - this is needed when the chat input's
    // parent components are initially hidden in React and then become visible again
    const resizeObserver = new ResizeObserver(() => {
      adjustTextareaHeight();

      // Set focus to textarea if it doesn't already have focus
      if (document.activeElement !== textarea) {
        textarea.focus();
      }
    });

    resizeObserver.observe(textarea);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          adjustTextareaHeight();
        }
      },
      { threshold: 0 }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);

  const handleSubmit = () => {
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
      // Reset height after sending
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleStartNewConversation = () => {
    onStartNewConversation();
  };

  const handleShowHistory = () => {
    onHistory();
  };

  const createSpeechRecognition = () => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    // Configuration
    recognition.continuous = false; // Stop after one utterance
    recognition.interimResults = false; // Only final results
    recognition.maxAlternatives = 1; // Just the best result
    recognition.lang = chatLanguage;

    return recognition;
  };

  const handleMicrophoneClick = () => {
    if (!isSpeechSupported) return;

    if (isListening) {
      // Stop listening
      recognitionRef.current?.stop();
      return;
    }

    // Create or get recognition instance
    if (!recognitionRef.current) {
      recognitionRef.current = createSpeechRecognition();
    }

    const recognition = recognitionRef.current;

    // Event handlers
    recognition.onstart = () => {
      setIsListening(true);
      DEBUG.log('ChatInput.handleMicrophoneClick', 'Speech recognition started');
    };

    recognition.onresult = (event: any) => {
      // Get the transcript (only final results since interimResults = false)
      const transcript = event.results[0][0].transcript;

      // Update message with transcript
      setMessage((prevMessage) => {
        // Append with space if there's existing text, otherwise replace
        const newMessage =
          prevMessage && prevMessage.trim() ? prevMessage + ' ' + transcript : transcript;

        // Auto-send the message after a short delay
        setTimeout(() => {
          if (newMessage.trim()) {
            onSendMessage(newMessage);
            setMessage('');
            // Reset textarea height
            if (textareaRef.current) {
              textareaRef.current.style.height = 'auto';
            }
          }
        }, 100);

        return newMessage;
      });
    };

    recognition.onerror = (event: any) => {
      DEBUG.log('ChatInput.handleMicrophoneClick', `Speech recognition error: ${event.error}`);
      setIsListening(false);
      // No user-facing error handling - just clean up silently
    };

    recognition.onend = () => {
      setIsListening(false);
      DEBUG.log('ChatInput.handleMicrophoneClick', 'Speech recognition ended');
    };

    // Start recognition
    try {
      recognition.start();
    } catch (error) {
      DEBUG.log('ChatInput.handleMicrophoneClick', `Failed to start recognition: ${error}`);
      setIsListening(false);
    }
  };

  return (
    <div ref={containerRef} className={styles.inputContainer}>
      <div className={styles.inputBox}>
        <div className={styles.inputRow}>
          <label htmlFor="messageInput" className={styles.visuallyHidden}>
            {t('Type a message')}
          </label>
          <textarea
            ref={(el) => {
              if (!el) return;
              textareaRef.current = el;
            }}
            id="messageInput"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            className={styles.messageInput}
            placeholder={t('Ask me anything about your visit...')}
            aria-label="Type a message"
            rows={1}
          />
        </div>
        <div className={styles.inlineButtonsContainer}>
          <div className={styles.leftButtons}>
            <button
              className={styles.inlineButton}
              aria-label="History"
              onClick={handleShowHistory}
            >
              <img
                src="/static/images/map/history.svg"
                className={styles.inlineButtonIcon}
                alt=""
              />
              {t('History')}
            </button>
            <button
              className={styles.inlineButton}
              aria-label="Start New Conversation"
              onClick={handleStartNewConversation}
            >
              <img
                src="/static/images/map/new-conversation.svg"
                className={styles.inlineButtonIcon}
                alt=""
              />
              {t('New')}
            </button>
          </div>
          <div className={styles.rightButtons}>
            {isSpeechSupported && (
              <button
                className={`${styles.micButton} ${isListening ? styles.listening : ''}`}
                aria-label="Voice input"
                onClick={handleMicrophoneClick}
              >
                <img src="/static/images/ic_mic.svg" className={styles.micButtonIcon} alt="" />
              </button>
            )}
            <button className={styles.sendButton} aria-label="Send message" onClick={handleSubmit}>
              <img src="/static/images/map/mail-send.svg" alt="" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

ChatInput.displayName = 'ChatInput';
